import { format, parse, isValid } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import i18n from '../i18n';

const currentLanguage = localStorage.getItem('language') || 'en';

export const ALLOWED_IMAGE_TYPES = {
  'image/jpeg': { extensions: ['jpg', 'jpeg', 'jfif', 'pjpeg', 'pjp'], label: 'JPG' },
  'image/png':  { extensions: ['png'],         label: 'PNG' },
  'image/gif':  { extensions: ['gif'],         label: 'GIF' },
  'image/webp': { extensions: ['webp'],        label: 'WebP' },
};

export const ALLOWED_DOCUMENT_TYPES = {
  'application/pdf': { extensions: ['pdf'], label: 'PDF' },
  'application/msword': { extensions: ['doc'], label: 'DOC' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { extensions: ['docx'], label: 'DOCX' },
  'application/vnd.ms-excel': { extensions: ['xls'], label: 'XLS' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { extensions: ['xlsx'], label: 'XLSX' },
  'application/vnd.ms-powerpoint': { extensions: ['ppt'], label: 'PPT' },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': { extensions: ['pptx'], label: 'PPTX' },
  'text/plain': { extensions: ['txt'], label: 'TXT' },
  'text/csv':   { extensions: ['csv'], label: 'CSV' },
  'application/rtf': { extensions: ['rtf'], label: 'RTF' },
  'application/vnd.oasis.opendocument.text': { extensions: ['odt'], label: 'ODT' },
  'application/vnd.oasis.opendocument.spreadsheet': { extensions: ['ods'], label: 'ODS' },
  'application/vnd.oasis.opendocument.presentation': { extensions: ['odp'], label: 'ODP' },
};

export const ALLOWED_ARCHIVE_TYPES = {
  'application/zip':              { extensions: ['zip'], label: 'ZIP' },
  'application/x-zip-compressed': { extensions: ['zip'], label: 'ZIP' },
  'application/x-zip':            { extensions: ['zip'], label: 'ZIP' },
  'application/vnd.rar':          { extensions: ['rar'], label: 'RAR' },
  'application/x-rar-compressed': { extensions: ['rar'], label: 'RAR' },
};

// Toutes les combinaisons possibles
export const ALLOWED_ALL_TYPES = {
  ...ALLOWED_IMAGE_TYPES,
  ...ALLOWED_DOCUMENT_TYPES,
  ...ALLOWED_ARCHIVE_TYPES,
};

// ─── Limites (cohérentes avec multer) ────────────────────────────────────────

export const LIMITS = {
  IMAGE_MAX_SIZE:    3 * 1024 * 1024,   //  3 MB  (logo, avatar)
  DOCUMENT_MAX_SIZE: 20 * 1024 * 1024,  // 20 MB  (documents, archives)
  MAX_FILES:         10,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Extrait l'extension d'un fichier en minuscules.
 */
const getExtension = (filename) =>
  filename.split('.').pop()?.toLowerCase() ?? '';

/**
 * Formate une taille en octets en chaîne lisible.
 */
export const formatSize = (bytes) => {
  if (bytes < 1024)        return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

/**
 * Retourne les labels lisibles des types autorisés.
 * Ex: ['JPG', 'PNG', 'WebP']
 */
export const getAllowedLabels = (allowedTypes) =>
  [...new Set(Object.values(allowedTypes).map((t) => t.label))];

const tHelper = (key, values = {}) => i18n.t(key, values);

// const formatDateWithLocale = (date, language) => {
//   const locales = { en: enUS, fr: fr }; 
//   const selectedLocale = locales[language] || enUS;

//   return format(new Date(date), 'MMM d, yyyy', { locale: selectedLocale });
// };

/**
 * Format a number with commas as thousand separators.
 * 
 * @param {number|string} number 
 * @returns {string}
 */
export function formatNumber(number) {
  if (number !== null && number !== undefined) {
    const separator = ',';
    const numberString = number.toString();
    const parts = numberString.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    return parts.join('.');
  }
  return '';
}

export function getLocalStorageItemWithExpiration(key) {
  const now = new Date().getTime();
  const expirationDate = localStorage.getItem('expirationDate');

  if (expirationDate && now > expirationDate) {
    // Si la date d'expiration est passée, supprimer les données
    localStorage.removeItem('rememberMe');
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('expirationDate');
    return null;
  }

  return localStorage.getItem(key);
}


export function formatDate(timestamp) {
  const date = new Date(timestamp);
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: currentLanguage === 'en' // 12-hour format for English, 24-hour for French
  };

  const locale = currentLanguage === 'fr' ? 'fr-FR' : 'en-US';

  // Format and capitalize the month for French
  let formattedDate = date.toLocaleString(locale, options);
  if (currentLanguage === 'fr') {
    // Extracting day, month, and year by splitting
    const [day, month, year, time] = formattedDate.split(/,?\s+/);
    const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);

    // Reassemble with the capitalized month
    formattedDate = `${day} ${capitalizedMonth} ${year}, ${time}`;
  }

  return formattedDate?.replace('.', '');
}


export const formatDateValue = (date, currentLanguage) => {
  const dateValues = new Date(date);

  // Define options for date formatting based on the selected language
  const dateOptions = { day: 'numeric', month: 'short', year: 'numeric' };

  // Define options for time formatting
  const timeOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: currentLanguage === 'en'
  };

  // Format the date and time
  const formattedDate = dateValues.toLocaleDateString(currentLanguage === 'fr' ? 'fr-FR' : 'en-US', dateOptions);
  const formattedTime = dateValues.toLocaleTimeString(currentLanguage === 'fr' ? 'fr-FR' : 'en-US', timeOptions);

  // Capitalize the first letter of the month for French
  const capitalizeMonth = (dateStr) => {
    return dateStr.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const finalDate = currentLanguage === 'fr'
    ? capitalizeMonth(formattedDate) // Capitalize first letter for French date
    : formattedDate;

  // Combine formatted date and time
  return currentLanguage === 'fr'
    ? `${finalDate?.replace('.', '')} à ${formattedTime}` // French format
    : `${finalDate} ${formattedTime}`; // English format
};

export const capitalizeFirstLetter = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export function parseDateString(dateString) {
  const [day, month, year] = dateString.split('/');
  return new Date(`${year}-${month}-${day}`);
}

export function parseDateStringValue(dateString) {
  const [day, month, year] = dateString.split('/');
  return `${year}-${month}-${day}`;
}


export const formatPrice = (price, currentLanguage) => {
  if (price === 0) {
    return tHelper('common.free');
  } else {
    const formattedPrice = new Intl.NumberFormat(currentLanguage === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: 'USD',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);

    return `${formattedPrice}`;
  }
};


export const formatEventStartEndDate = (event, t) => {
  const currentLocale = currentLanguage === 'fr' ? fr : enUS;

  const startDate = event?.startDate ? new Date(event.startDate) : null;
  const endDate = event?.endDate ? new Date(event.endDate) : null;

  const startTime = event?.startTime; // Assuming startTime is in format 'hh:mm AM/PM'
  const endTime = event?.endTime; // Assuming endTime is in format 'hh:mm AM/PM'

  // Format the start date
  const formattedStartDate = startDate && isValid(startDate)
    ? format(startDate, currentLanguage === 'fr' ? 'eee dd MMM yyyy' : 'eee, MMM d, yyyy', { locale: currentLocale })
    : t("event.comingSoon");

  // Format the end date
  const formattedEndDate = endDate && isValid(endDate)
    ? format(endDate, currentLanguage === 'fr' ? 'eee dd MMM yyyy' : 'eee, MMM d, yyyy', { locale: currentLocale })
    : startDate && isValid(startDate)
      ? format(startDate, currentLanguage === 'fr' ? 'eee dd MMM yyyy' : 'eee, MMM d, yyyy', { locale: currentLocale })
      : t("event.comingSoon");

  // Function to format time from 'hh:mm AM/PM'
  const formatTimeFromString = (time) => {
    if (!time) return ''; // If no time is provided, return an empty string

    // Parse the time and set hours and minutes
    const [timePart, modifier] = time.split(' '); // Split the time and AM/PM part
    let [hours, minutes] = timePart.split(':').map(Number); // Split the time into hours and minutes

    // Convert to 24-hour format if it's PM and hours are not 12
    if (modifier === 'PM' && hours < 12) {
      hours += 12;
    }
    // Handle the case for 12 AM
    if (modifier === 'AM' && hours === 12) {
      hours = 0;
    }

    // Create a new Date object for formatting the time
    const date = new Date(startDate); // Use startDate or endDate as needed
    date.setHours(hours, minutes); // Set the hours and minutes

    const timeOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: currentLanguage === 'en', // Use 12-hour format for English
      hourCycle: 'h11' // Set to 12-hour format for en-US
    };

    let formattedTime = date.toLocaleTimeString(currentLanguage === 'fr' ? 'fr-FR' : 'en-US', timeOptions);
    if (currentLanguage === 'fr') {
      formattedTime = formattedTime.replace(':', 'H'); // Change ':' to 'H' for French
    }

    return formattedTime;
  };

  // Capitalize the first letter of the formatted date for French
  const capitalizeFirstLetter = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  // Construct the final output with the correct formatting
  return {
    formattedStart: (currentLanguage === 'fr' && isValid(startDate))
      ? `${capitalizeFirstLetter(formattedStartDate.replace('.', ''))?.replace('.', '')} à ${formatTimeFromString(startTime)}`
      : `${capitalizeFirstLetter(formattedStartDate.replace('.', ''))?.replace('.', '')} ${formatTimeFromString(startTime)}`,

    formattedEnd: (currentLanguage === 'fr' && isValid(endDate))
      ? `${capitalizeFirstLetter(formattedEndDate.replace('.', ''))?.replace('.', '')} à ${formatTimeFromString(endTime)}`
      : `${capitalizeFirstLetter(formattedEndDate.replace('.', ''))?.replace('.', '')} ${formatTimeFromString(endTime)}`,
  };
};

export function formatEventTime(startDate, endDate, startTime, endTime, t) {
  const locale = currentLanguage === 'fr' ? fr : enUS;

  if (!startDate || !endDate || !startTime || !endTime || startTime === '' || endTime === '') {
    return tHelper('event.time.allday');
  } else {
    const startDateTime = new Date(startDate);
    const endDateTime = new Date(endDate);

    // Check if the start and end dates are the same
    if (startDateTime.getDate() === endDateTime.getDate() &&
      startDateTime.getMonth() === endDateTime.getMonth() &&
      startDateTime.getFullYear() === endDateTime.getFullYear()) {
      const gmtOffset = -startDateTime.getTimezoneOffset() / 60; // GMT offset in hours
      const gmt = `GMT${gmtOffset >= 0 ? `+${gmtOffset}` : gmtOffset}`; // Format GMT offset

      const formattedStartTime = format(parse(startTime, 'h:mm a', new Date()), currentLanguage === 'fr' ? 'H:mm' : 'h:mm a', { locale });
      const formattedEndTime = format(parse(endTime, 'h:mm a', new Date()), currentLanguage === 'fr' ? 'H:mm' : 'h:mm a', { locale });

      return currentLanguage === 'fr'
        ? `De ${formattedStartTime.replace(':', 'h')} à ${formattedEndTime.replace(':', 'h')} ${gmt}` // French format with 'h'
        : `${formattedStartTime} - ${formattedEndTime} ${gmt}`; // English format
    } else {
      const parsedTime = parse(startTime, 'h:mm a', new Date());
      const formattedParsedTime = format(parsedTime, currentLanguage === 'fr' ? 'H:mm' : 'h:mm a', { locale });

      return currentLanguage === 'fr'
        ? formattedParsedTime.replace(':', 'h').toUpperCase() // For French format: replace ':' with 'h'
        : formattedParsedTime.toUpperCase(); // For English format
    }
  }
}

function capitalizeAndClean(dateString) {
  return dateString.charAt(0).toUpperCase() + dateString.slice(1);
}


export function formatEventDate(startDate, endDate, t) {
  const locale = currentLanguage === 'fr' ? fr : enUS;

  if (!startDate || !endDate) {
    return t("event.comingSoon");
  } else {
    const startDateTime = new Date(startDate);
    const endDateTime = new Date(endDate);

    // Check if the start and end dates are the same
    if (startDateTime.getDate() === endDateTime.getDate() &&
      startDateTime.getMonth() === endDateTime.getMonth() &&
      startDateTime.getFullYear() === endDateTime.getFullYear()) {
      const formattedDate = format(startDateTime, currentLanguage === 'fr' ? 'EEEE d MMMM yyyy' : 'EEEE, MMMM d, yyyy', { locale });
      return capitalizeAndClean(formattedDate);
    } else {
      const formattedStartDate = format(startDateTime, currentLanguage === 'fr' ? 'EEE d MMMM yyyy' : 'EEE, MMM d, yyyy', { locale });
      return capitalizeAndClean(formattedStartDate)?.replace('.', '');
    }
  }
}


export function formatEventDateTime(startDate, endDate, startTime, endTime, t) {
  const locale = currentLanguage === 'fr' ? fr : enUS;

  // Check for missing values
  if (!startDate || !endDate || !startTime || !endTime || startTime === '' || endTime === '') {
    return t("event.comingSoon");
  }

  try {
    const parsedStartTime = parse(startTime, 'h:mm a', new Date());
    const parsedEndTime = parse(endTime, 'h:mm a', new Date());

    // Format time based on language
    const formattedStartTime = format(parsedStartTime, currentLanguage === 'fr' ? 'HH:mm' : 'ha', { locale }).toLowerCase();
    const formattedEndTime = format(parsedEndTime, currentLanguage === 'fr' ? 'HH:mm' : 'ha', { locale }).toLowerCase();

    const startDateTime = new Date(startDate);
    const endDateTime = new Date(endDate);

    // Calculate GMT offset in hours
    const gmtOffset = -startDateTime.getTimezoneOffset() / 60; // Offset in hours
    const gmt = `GMT${gmtOffset >= 0 ? `+${gmtOffset}` : gmtOffset}`;

    // Check if start and end dates are the same
    if (startDateTime.getDate() === endDateTime.getDate() &&
      startDateTime.getMonth() === endDateTime.getMonth() &&
      startDateTime.getFullYear() === endDateTime.getFullYear()) {

      // Format date for the same day
      const formattedDate = format(startDateTime, currentLanguage === 'fr' ? 'EEEE d MMMM' : 'EEEE, MMMM d', { locale });

      // Capitalize the first letter of the day and month correctly
      const formattedDateCapitalized = formattedDate.replace(/^(.)/, (char) => char.toUpperCase()).replace(/(\b\w)/g, (char, index) => {
        if (currentLanguage === 'fr' && index > 0 && formattedDate[index - 1] === ' ') {
          return char.toUpperCase(); // Capitalize the first letter after a space for French
        }
        return char; // Keep other characters as they are
      });

      return `${formattedDateCapitalized.replace(/\./g, '')} • ${formattedStartTime} - ${formattedEndTime} ${gmt}`;
    } else {
      // Format date for different days
      const formattedStartDate = format(startDateTime, currentLanguage === 'fr' ? 'EEE d MMM yyyy' : 'EEE, MMM d, yyyy', { locale });

      // Capitalize the first letter correctly for French dates
      const formattedStartDateCapitalized = formattedStartDate.replace(/^(.)/, (char) => char.toUpperCase()).replace(/(\b\w)/g, (char, index) => {
        if (currentLanguage === 'fr' && index > 0 && formattedStartDate[index - 1] === ' ') {
          return char.toUpperCase(); // Capitalize the first letter after a space for French
        }
        return char; // Keep other characters as they are
      });

      return `${formattedStartDateCapitalized.replace(/\./g, '')} • ${formattedStartTime} ${gmt}`;
    }
  } catch (error) {
    console.error('Error formatting date/time:', error);
    return tHelper('event.datetime.invalid');
  }
}

/**
 * Valide un fichier image (logo, avatar, photo).
 */
export const validateImageFile = (file, options = {}) => {
  const { maxSize = LIMITS.IMAGE_MAX_SIZE } = options;

  if (!file) {
    return { valid: false, error: tHelper('file.validation.noFile') };
  }

  // 1. Type MIME
  if (!ALLOWED_IMAGE_TYPES[file.type]) {
    const allowed = getAllowedLabels(ALLOWED_IMAGE_TYPES).join(', ');
    return {
      valid: false,
      error: tHelper('file.validation.unsupportedFormat', { allowed }),
    };
  }

  // 2. Cohérence extension / MIME
  const ext = getExtension(file.name);
  const expectedExts = ALLOWED_IMAGE_TYPES[file.type]?.extensions ?? [];
  if (!expectedExts.includes(ext)) {
    return {
      valid: false,
      error: tHelper('file.validation.extensionMismatch', { ext }),
    };
  }

  // 3. Taille
  if (file.size > maxSize) {
    return {
      valid: false,
      error: tHelper('file.validation.tooLarge', { maxSize: formatSize(maxSize) }),
    };
  }

  // 4. Taille minimale (fichier vide / corrompu)
  if (file.size === 0) {
    return { valid: false, error: tHelper('file.validation.empty') };
  }

  return { valid: true };
};

/**
 * Valide un fichier document (PDF, Word, Excel, etc.).
 */
export const validateDocumentFile = (file, options = {}) => {
  const { maxSize = LIMITS.DOCUMENT_MAX_SIZE } = options;

  if (!file) {
    return { valid: false, error: tHelper('file.validation.noFile') };
  }

  if (!ALLOWED_DOCUMENT_TYPES[file.type]) {
    const allowed = getAllowedLabels(ALLOWED_DOCUMENT_TYPES).join(', ');
    return {
      valid: false,
      error: tHelper('file.validation.unsupportedFormat', { allowed }),
    };
  }

  const ext = getExtension(file.name);
  const expectedExts = ALLOWED_DOCUMENT_TYPES[file.type]?.extensions ?? [];
  if (!expectedExts.includes(ext)) {
    return {
      valid: false,
      error: tHelper('file.validation.extensionMismatch', { ext }),
    };
  }

  if (file.size > maxSize) {
    return {
      valid: false,
      error: tHelper('file.validation.tooLarge', { maxSize: formatSize(maxSize) }),
    };
  }

  if (file.size === 0) {
    return { valid: false, error: tHelper('file.validation.empty') };
  }

  return { valid: true };
};

/**
 * Valide un fichier quelconque (image OU document OU archive).
 */
export const validateAnyFile = (file, options = {}) => {
  const { maxSize = LIMITS.DOCUMENT_MAX_SIZE } = options;

  if (!file) {
    return { valid: false, error: tHelper('file.validation.noFile') };
  }

  if (!ALLOWED_ALL_TYPES[file.type]) {
    const allowed = getAllowedLabels(ALLOWED_ALL_TYPES).join(', ');
    return {
      valid: false,
      error: tHelper('file.validation.unsupportedFormat', { allowed }),
    };
  }

  const ext = getExtension(file.name);
  const expectedExts = ALLOWED_ALL_TYPES[file.type]?.extensions ?? [];
  if (!expectedExts.includes(ext)) {
    return {
      valid: false,
      error: tHelper('file.validation.extensionMismatch', { ext }),
    };
  }

  if (file.size > maxSize) {
    return {
      valid: false,
      error: tHelper('file.validation.tooLarge', { maxSize: formatSize(maxSize) }),
    };
  }

  if (file.size === 0) {
    return { valid: false, error: tHelper('file.validation.empty') };
  }

  return { valid: true };
};

/**
 * Valide une liste de fichiers (upload multiple).
 * Retourne { valid, errors: [{ filename, error }] }
 */
export const validateMultipleFiles = (files, validator = validateAnyFile, options = {}) => {
  const fileArray = Array.from(files);

  if (fileArray.length === 0) {
    return {
      valid: false,
      errors: [{ filename: null, error: tHelper('file.validation.noFile') }],
    };
  }

  if (fileArray.length > LIMITS.MAX_FILES) {
    return {
      valid: false,
      errors: [{ filename: null, error: tHelper('file.validation.tooManyFiles', { maxFiles: LIMITS.MAX_FILES }) }],
    };
  }

  const errors = [];
  for (const file of fileArray) {
    const result = validator(file, options);
    if (!result.valid) {
      errors.push({ filename: file.name, error: result.error });
    }
  }

  return { valid: errors.length === 0, errors };
};

export const getUserColumnName = (selectedOption) => {
  const columnMapping = {
    "Display Name": "displayName",
    "Email": "email",
    "Role": "role",
    "Status": "status",
    "Date Created": "dateCreated",
    "Last Login": "lastLogin"
  };

  return columnMapping[selectedOption] || "dateCreated"; // Valeur par défaut
};

// Validation côté client robuste
export function validateForm(data) {
  const errors = {};

  // Protection contre l'injection
  const maliciousPatterns = /(<script|javascript:|onload=|onerror=)/i;
  Object.values(data).forEach(value => {
    if (maliciousPatterns.test(value)) {
      throw new Error('Entrée suspecte détectée');
    }
  });

  return errors;
}

// Échapper le HTML
export function escapeHTML(str) {
  return str.replace(/[&<>"']/g, m => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[m]));
}

export const secureStorage = {
  setItem: (key, value) => {
    const encrypted = btoa(unescape(encodeURIComponent(value)));
    localStorage.setItem(key, encrypted);
  },
  getItem: (key) => {
    const encrypted = localStorage.getItem(key);
    if (!encrypted) return null;
    try {
      return decodeURIComponent(escape(atob(encrypted)));
    } catch {
      return null;
    }
  },
  removeItem: (key) => {
    localStorage.removeItem(key);
  }
};

export function generateCSRFToken() {
  let csrfToken = Math.random().toString(36).substring(2, 15) +
                  Math.random().toString(36).substring(2, 15);
  return csrfToken;
}

export function stripScripts(input) {
  return input
    .replace(/<\s*script[^>]*>.*?<\s*\/\s*script\s*>/gis, '')
    .replace(/javascript:\s*/gi, '')
    .replace(/data:text\/html[^,]*,/gi, '');
}

export function sanitizePayload(payload) {
  const out = {};
  for (const [k, v] of Object.entries(payload)) {
    out[k] = typeof v === 'string' ? stripScripts(v) : v;
  }
  return out;
}


